import React, { useEffect, useState } from 'react';
import { FeedbackCategoryOption, useGetFeedbackCategories, useSubmitAgentFeedback } from '@components/Copilot/hooks';
import Like from '@components/Copilot/Like';
import { ReactionType } from '@components/Copilot/types';
import { useAvContext } from '@context/AvContextProvider';
import { AvButton, AvCheckbox, AvDialog, CopyToClipboard, CopyType, FeatureFlags, TextInput } from '@fabric-ui/components';
import { Comment } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Divider } from '@mui/material';

enum FeedbackPhase {
  Idle = 'idle',
  Liked = 'liked',
  DislikeForm = 'dislikeForm',
  Submitted = 'submitted',
}

const THANK_YOU_HIDE_DELAY_MS = 3_000;

const ThankYouMessage = ({ onHide }: { onHide: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onHide, THANK_YOU_HIDE_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        mt: 1,
        p: '24px 16px',
        borderRadius: '8px',
        border: '1px solid var(--color-border-base-secondary)',
        backgroundColor: 'var(--color-background-primary)',
        color: 'var(--color-content-base-primary)',
        fontSize: 14,
        textAlign: 'center',
      }}>
      Thank you for your feedback!
    </Box>
  );
};

interface CopilotMessageFeedbackProps {
  formattedTimestamp: string;
  message: string;
  loopId: string;
  sessionId: string;
}

const CopilotMessageFeedback: React.FC<CopilotMessageFeedbackProps> = ({ formattedTimestamp, message, loopId, sessionId }) => {
  const { featureFlags } = useAvContext();
  const [phase, setPhase] = useState<FeedbackPhase>(FeedbackPhase.Idle);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [negativeFeedbackText, setNegativeFeedbackText] = useState('');
  const [draftDetails, setDraftDetails] = useState<string | null>(null);

  const { data: feedbackCategories = [] as FeedbackCategoryOption[] } = useGetFeedbackCategories();
  const { mutate: submitFeedback, isPending: isSubmitting } = useSubmitAgentFeedback({
    onSuccess: () => setPhase(prev => (prev === FeedbackPhase.DislikeForm ? FeedbackPhase.Submitted : prev)),
  });

  const handleLike = () => {
    setPhase(FeedbackPhase.Liked);
    submitFeedback({ feedbackInput: { loopId, sessionId, reactionType: ReactionType.LIKED } });
  };

  const handleDislike = () => {
    setPhase(FeedbackPhase.DislikeForm);
  };

  const toggleCategory = (key: string, checked: boolean) =>
    setSelectedCategories(prev => (checked ? [...prev, key] : prev.filter(k => k !== key)));

  const canSubmit = selectedCategories.length > 0 || negativeFeedbackText.trim().length > 0;

  const submitDislikeFeedback = (freeText: string) => {
    const trimmed = freeText.trim();
    if (selectedCategories.length === 0 && trimmed.length === 0) {
      return;
    }
    submitFeedback({
      feedbackInput: {
        loopId,
        sessionId,
        reactionType: ReactionType.DISLIKED,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        freeText: trimmed ?? undefined,
      },
    });
  };

  const handleSubmit = () => submitDislikeFeedback(negativeFeedbackText);

  const openDetailsDialog = () => setDraftDetails(negativeFeedbackText);

  const closeDetailsDialog = () => setDraftDetails(null);

  const saveDetails = () => {
    setNegativeFeedbackText(draftDetails!);
    setDraftDetails(null);
    submitDislikeFeedback(draftDetails!);
  };

  return (
    <>
      <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
        {sessionId &&
          featureFlags[FeatureFlags.EnableAgentFeedback] && ( // TODO: remove session id check once remediation copilot is on new infra
            <>
              <Like
                onClick={handleLike}
                selected={phase === FeedbackPhase.Liked}
                disabled={isSubmitting || phase === FeedbackPhase.Liked}
              />
              <Like
                reversed
                onClick={handleDislike}
                selected={phase === FeedbackPhase.DislikeForm || phase === FeedbackPhase.Submitted}
                disabled={isSubmitting || phase === FeedbackPhase.DislikeForm || phase === FeedbackPhase.Submitted}
              />
            </>
          )}
        <CopyToClipboard copyData={message} copyType={CopyType.DATA} />
        {phase !== FeedbackPhase.DislikeForm && (
          <Divider
            flexItem
            orientation="vertical"
            sx={{ height: 14, alignSelf: 'center', borderColor: 'var(--color-border-base-secondary)' }}
          />
        )}
        <Box sx={{ fontSize: 12, color: 'var(--color-content-base-tertiary)' }}>{formattedTimestamp}</Box>
      </Box>

      {phase === FeedbackPhase.DislikeForm && (
        <Box
          sx={{
            ...flex.col,
            gap: 2,
            p: 2,
            mt: 1,
            borderRadius: '8px',
            border: '1px solid var(--color-border-base-secondary)',
            backgroundColor: 'var(--color-background-primary)',
          }}>
          <Box sx={{ fontSize: 14, color: 'var(--color-content-base-secondary)' }}>Tell us more:</Box>
          <Box sx={{ ...flex.col, gap: 1 }}>
            {feedbackCategories.map(option => (
              <AvCheckbox
                key={option.key}
                size="small"
                label={option.label}
                value={selectedCategories.includes(option.key)}
                onChange={(checked: boolean) => toggleCategory(option.key, checked)}
              />
            ))}
          </Box>
          <Box sx={{ ...flex.itemsCenter, justifyContent: 'flex-end', gap: 1 }}>
            <AvButton size="small" variant="outlined" onClick={openDetailsDialog}>
              <Box sx={{ ...flex.itemsCenter, gap: 0.5 }}>
                <Comment style={iconSize(16)} />
                Add Details
              </Box>
            </AvButton>
            <AvButton size="small" onClick={handleSubmit} disabled={!canSubmit || isSubmitting} loading={isSubmitting} variant="contained">
              Submit
            </AvButton>
          </Box>
        </Box>
      )}

      {phase === FeedbackPhase.Submitted && <ThankYouMessage onHide={() => setPhase(FeedbackPhase.Idle)} />}

      <AvDialog
        open={draftDetails !== null}
        onClose={closeDetailsDialog}
        title="Add Details"
        noPadding
        size="md"
        footer={
          <Box sx={{ ...flex.itemsCenter, justifyContent: 'flex-end', gap: 1 }}>
            <AvButton variant="outlined" size="small" onClick={closeDetailsDialog}>
              Cancel
            </AvButton>
            <AvButton size="small" onClick={saveDetails} disabled={!draftDetails?.trim()} variant="contained">
              Submit
            </AvButton>
          </Box>
        }>
        <Box sx={{ '& .MuiFormControl-root': { maxWidth: '100%' }, px: 2 }}>
          <TextInput
            value={draftDetails ?? ''}
            onChange={setDraftDetails}
            placeholder="What could be better?"
            multiline
            minRows={5}
            fullWidth
            disableClearButton
          />
        </Box>
      </AvDialog>
    </>
  );
};

export default CopilotMessageFeedback;
